import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiTrash2,
  FiPlus,
  FiDollarSign,
  FiCreditCard,
  FiCalendar,
  FiChevronUp,
  FiChevronDown,
  FiRotateCcw,
  FiFileText,
  FiSearch,
  FiX,
  FiEye,
  FiPrinter,
  FiEdit2,
  FiRotateCw
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './sales.css';

const Sales = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1
  });
  const [dateSort, setDateSort] = useState('desc');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Temporary search input value
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [printAfterOpen, setPrintAfterOpen] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [invoiceToRevert, setInvoiceToRevert] = useState(null);
  const [reverting, setReverting] = useState(false);
  const searchInputRef = useRef(null);

   const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: dateSort,
        invoice_search: invoiceSearch // Using the actual search value here
      };

      if (dateFilter.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter.endDate) params.end_date = dateFilter.endDate;

      const response = await axios.get('/sales', { params });
      
      setInvoices(response.data.data);
      setPagination(prev => ({
        ...prev,
        totalItems: response.data.pagination.total_items,
        totalPages: response.data.pagination.total_pages
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, dateSort, dateFilter.startDate, dateFilter.endDate, invoiceSearch]);

  // Handle input change - updates temporary value only
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle Enter key press - commits the search
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setInvoiceSearch(searchQuery); // This will trigger the actual search
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  // Clear search - resets both temporary and actual values
  const clearInvoiceSearch = () => {
    setSearchQuery('');
    setInvoiceSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Trigger fetch when invoiceSearch changes
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (showInvoiceModal && invoicePreview && printAfterOpen) {
      const printTimer = setTimeout(() => {
        window.print();
        setPrintAfterOpen(false);
      }, 150);

      return () => clearTimeout(printTimer);
    }
  }, [showInvoiceModal, invoicePreview, printAfterOpen]);

  const handleDeleteClick = (id) => {
    setInvoiceToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/sales/${invoiceToDelete}`);
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete invoice');
    } finally {
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setInvoiceToDelete(null);
  };

  const handleRevertClick = (invoiceNo) => {
    setInvoiceToRevert(invoiceNo);
    setShowRevertModal(true);
  };

  const handleConfirmRevert = async () => {
    try {
      setReverting(true);
      await axios.put(`/sales/${invoiceToRevert}/revert-to-credit`);
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revert credit settlement');
    } finally {
      setReverting(false);
      setShowRevertModal(false);
      setInvoiceToRevert(null);
    }
  };

  const handleCancelRevert = () => {
    setShowRevertModal(false);
    setInvoiceToRevert(null);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const toggleDateSort = () => {
    setDateSort(dateSort === 'desc' ? 'asc' : 'desc');
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter({
      ...dateFilter,
      [name]: value
    });
  };

  const clearDateFilters = () => {
    setDateFilter({
      startDate: '',
      endDate: ''
    });
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getPaymentLabel = (invoice) => {
    const totalAmount = Number(invoice?.total_amount || 0);
    const paidAmount = Number(invoice?.paid_amount || 0);
    const methodLabel = invoice?.payment_type === 'bank'
      ? '🏦 Bank'
      : invoice?.payment_type === 'cheque'
      ? '📋 Cheque'
      : '💵 Cash';

    if (paidAmount <= 0 && totalAmount > 0) return 'Credit';
    if (paidAmount < totalAmount) return `${methodLabel} + Credit`;
    return methodLabel;
  };

  const handleViewInvoice = async (invoiceId, shouldPrint = false) => {
    try {
      const response = await axios.get(`/sales/invoice/${invoiceId}`);
      setInvoicePreview(response.data);
      setShowInvoiceModal(true);
      setPrintAfterOpen(shouldPrint);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load invoice');
    }
  };

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="sales-container">
      <div className="sales-header">
        <h1 className="sales-title">Sales Invoices</h1>
        <button 
          className="add-sale-btn"
          onClick={() => navigate('/sales/add')}
        >
          <FiPlus />
          New Sales
        </button>
      </div>

      <div className="filters-container">
        <div className="search-filter">
         <div className="search-input-container">
        <FiSearch className="search-icon" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search by invoice number (press Enter)"
          value={searchQuery} // Using temporary value here
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
        />
        {(searchQuery || invoiceSearch) && (
          <button 
            onClick={clearInvoiceSearch}
            className="clear-search-btn"
          >
            <FiX />
          </button>
        )}
      </div>
        </div>

        <div className="date-filters">
          <div className="filter-group">
            <label htmlFor="startDate">From:</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={dateFilter.startDate}
              onChange={handleDateFilterChange}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="endDate">To:</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={dateFilter.endDate}
              onChange={handleDateFilterChange}
            />
          </div>
          <button 
            className="clear-filters-btn"
            onClick={clearDateFilters}
          >
            Clear Dates
          </button>
        </div>
      </div>

    


      <table className="sales-table">
        <thead>
          <tr>
            <th>Invoice No</th>
            <th>Total Amount (Rs)</th>
            <th>Paid Amount (Rs)</th>
            <th>Retailer</th>
            <th>
              <div className="sortable-header" onClick={toggleDateSort}>
                <FiCalendar />
                Sale Date
                {dateSort === 'desc' ? (
                  <FiChevronDown className="sort-icon" />
                ) : (
                  <FiChevronUp className="sort-icon" />
                )}
              </div>
            </th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(invoice => (
            <tr key={invoice.invoice_id}>
              <td className="invoice-cell">
                <div className="invoice-number">
                  <FiFileText className="invoice-icon" />
                  {invoice.invoice_no}
                </div>
              </td>
              <td>{invoice.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td>{invoice.paid_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td>{invoice.retailer_name || invoice.customer_name || 'Customer'}</td>
              <td>
                {new Date(invoice.sale_date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </td>
              <td>
                <span className={`payment-status ${invoice.paid_amount < invoice.total_amount ? 'credit' : 'paid'}`}>
                  {invoice.paid_amount < invoice.total_amount ? (
                    <>
                      <FiCreditCard /> Credit
                    </>
                  ) : (
                    <>
                      <FiDollarSign /> Paid
                    </>
                  )}
                </span>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => handleViewInvoice(invoice.invoice_id)}
                    className="view-btn"
                    title="View Invoice"
                  >
                    <FiEye size={16} />
                  </button>
               
                  <button
                    onClick={() => navigate(`/sales/return/${invoice.invoice_id}`)}
                    className="return-btn"
                    title="Return Items"
                  >
                    <FiRotateCcw size={16} />
                  </button>
                  <button
                    onClick={() => navigate(`/sales/edit/${invoice.invoice_id}`)}
                    className="edit-btn"
                    title="Edit Sale"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  {invoice.pre_settlement_paid_amount !== null && (
                    <button
                      onClick={() => handleRevertClick(invoice.invoice_no)}
                      className="revert-btn"
                      title="Undo credit settlement (mistakenly marked as paid)"
                    >
                      <FiRotateCw size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(invoice.invoice_id)}
                    className="delete-btn"
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This will also delete all associated sales records."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <ConfirmationModal
        show={showRevertModal}
        title="Undo Credit Settlement"
        message={
          reverting
            ? 'Reverting...'
            : `Undo the "Mark as Paid" for invoice ${invoiceToRevert}? It will go back to unpaid/credit status, and any bank deposit recorded for it will be reversed.`
        }
        onConfirm={handleConfirmRevert}
        onCancel={handleCancelRevert}
      />

      {invoices.length === 0 && (
        <div className="empty-state">
          <p>No invoices found matching your criteria.</p>
        </div>
      )}

      {showInvoiceModal && invoicePreview && (
        <div className="invoice-modal">
          <div className="invoice-modal-content">
            <h1>SK</h1>
            <div className="invoice-header">
              <h2>Invoice #{invoicePreview.invoice_no}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowInvoiceModal(false);
                  setInvoicePreview(null);
                }}
              >
                <FiX />
              </button>
            </div>

            <div className="invoice-details">
              <div className="invoice-meta">
                <div>
                  <strong>Date:</strong>{' '}
                  {new Date(invoicePreview.sale_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                <div>
                  <strong>Customer:</strong>{' '}
                  {invoicePreview.retailer_name || invoicePreview.customer_name || 'Customer'}
                </div>
              </div>

              <table className="invoice-items">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicePreview.items?.map((item, index) => (
                    <tr key={item.sale_id || index}>
                      <td>{item.item_name}</td>
                      <td>{item.quantity} {item.unit}</td>
                      <td>Rs{formatCurrency(item.normalized_price)}</td>
                      <td>Rs{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {invoicePreview.subtotal_amount != null && Number(invoicePreview.subtotal_amount) !== Number(invoicePreview.total_amount) && (
                    <tr>
                      <td colSpan="3" className="text-right"><strong>Subtotal:</strong></td>
                      <td>Rs{formatCurrency(invoicePreview.subtotal_amount)}</td>
                    </tr>
                  )}
                  {Number(invoicePreview.discount_amount) > 0 && (
                    <tr>
                      <td colSpan="3" className="text-right"><strong>Discount:</strong></td>
                      <td>-Rs{formatCurrency(invoicePreview.discount_amount)}</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan="3" className="text-right"><strong>Total:</strong></td>
                    <td>Rs{formatCurrency(invoicePreview.total_amount)}</td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="text-right"><strong>Paid Amount:</strong></td>
                    <td>Rs{formatCurrency(invoicePreview.paid_amount)}</td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="text-right"><strong>Payment Method:</strong></td>
                    <td>{getPaymentLabel(invoicePreview)}</td>
                  </tr>
                  {Number(invoicePreview.total_amount) > Number(invoicePreview.paid_amount) && (
                    <tr>
                      <td colSpan="3" className="text-right"><strong>Credit:</strong></td>
                      <td>Rs{formatCurrency(Number(invoicePreview.total_amount) - Number(invoicePreview.paid_amount))}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>

            <div className="invoice-actions">
              <button
                className="print-btn"
                onClick={() => window.print()}
              >
                <FiPrinter /> Print Invoice
              </button>
              <button
                className="done-btn"
                onClick={() => {
                  setShowInvoiceModal(false);
                  setInvoicePreview(null);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            const pageNum = pagination.page <= 3 
              ? i + 1 
              : Math.min(pagination.totalPages - 4, pagination.page - 2) + i;
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={pagination.page === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default Sales;
